import * as H from 'history';
import React, { ComponentType, FC, MouseEventHandler } from 'react';
import { NotifyFunction } from '@saucelabs/sl-container-notifications';
import { recordTrack } from '@saucelabs/sl-util-segmentio';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';
import { constants as userConstants } from '@saucelabs/sl-module-user';
import { useLocation } from 'react-router-dom';
import type { DataCenter } from '@saucelabs/sl-util-config';
import { useFocusVisible } from 'react-aria';
import classnames from 'classnames';

import * as constants from './constants';
import { V2_MENU_PARENTS } from './constants';
import { NAVIGATION_EVENTS } from './tracking';
import { SKIP_MENU_IDS } from './components/SkipMenu';

/**
 * Check if the current location includes the requested path / string.
 * @param location
 * @param path
 */
export function pathIncludes(location: H.Location, path: string): boolean {
  return location.pathname.includes(path);
}

/**
 * Check if the current location is a parent of the requested path.
 * @param location
 * @param menuItem
 */
export function isParentOf(
  location: H.Location,
  menuItem: typeof V2_MENU_PARENTS[number],
): boolean {
  if (!constants.V2_MENU_PARENTS.includes(menuItem)) {
    throw new Error(`This function does not support menuItem = ${menuItem}`);
  }

  if (
    (menuItem === constants.ANALYTICS && pathIncludes(location, '/analytics')) ||
    (menuItem === constants.LIVE_TESTING && pathIncludes(location, '/live'))
  ) {
    return true;
  }

  const patterns = {
    '/dashboard/builds': constants.AUTOMATED,
    '/dashboard/live': constants.LIVE_TESTING,
    '/getting-started': constants.GETTING_STARTED,
    '/dashboard/tests': constants.AUTOMATED,
    '/archives/vdc': constants.AUTOMATED,
    '/platform-configurator': constants.AUTOMATED,
  };

  const matchingPath = Object.keys(patterns).find((path) => pathIncludes(location, path));

  return matchingPath ? patterns[matchingPath] === menuItem : false;
}

export enum HeaderItemType {
  Account,
}

/**
 * Checks if the header item is active based on the current location path.
 * @param location
 * @param type
 */
export const isActiveItem = (location: ReturnType<typeof useLocation>, type: HeaderItemType) => {
  // eslint-disable-next-line default-case
  switch (type) {
    case HeaderItemType.Account: {
      const currentPath = ['/user-settings', '/team-management', '/integrations']
        .find((pa) => location.pathname.indexOf(pa) !== -1);
      if (currentPath) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Determines whether to show an IE 11 deprecation warning in IE browsers.
 * @param {function} notifyDanger - dispatch function that will render the Notification component
 */
export function showIE11WarningForIE(notifyDanger: NotifyFunction): void {
  const { userAgent } = window.navigator;
  // https://stackoverflow.com/a/19999868
  const browserIsIE = userAgent.indexOf('MSIE ') > 0 || /Trident.*rv:11\./.exec(userAgent);

  if (browserIsIE && !window.localStorage.getItem(constants.IE11_WARNING_DISMISSED_KEY)) {
    // 1609459200000 is 01/01/2021 @ 12:00am (UTC) in milliseconds from Epoch
    const firstSentence = Date.now() < 1609459200000 ?
      'Starting December 31, 2020, we will no longer support Internet Explorer 11 for the Sauce Labs web app.' :
      'Since December 31, 2020, we have stopped supporting Internet Explorer 11 for the Sauce Labs web app.';

    notifyDanger(`
      ${firstSentence}
      You will still be able to select IE11 for your tests,
      but for the best experience please log into Sauce Labs using a different browser.
      <br />
      <em>
        <a href="https://support.saucelabs.com/hc/en-us/articles/360056219674" target="_blank">Learn more about this decision.</a>
      </em>
    `, null, true, () => window.localStorage.setItem(constants.IE11_WARNING_DISMISSED_KEY, 'true'));
  }
}

/**
 * Keep search query string across analytics pages
 * @param {Object} currentLocation
 * @param {string} pathname
 * @returns {Object}
 */
export function getAnalyticsLink(
  currentLocation: H.Location,
  pathname: string,
): H.LocationDescriptor {
  const { search: currentSearch, pathname: currentPathname } = currentLocation;
  const search = currentPathname.indexOf('/analytics/') > -1 ? currentSearch : '';

  return {
    ...currentLocation,
    pathname,
    search,
  };
}

/**
 * Converts a label into a data-qa attribute.
 *
 * @param prefix
 * @param label
 */
export const labelToQA = ({ prefix, label }: {prefix: string, label: string}) => {
  if (!label) {
    throw Error('Please provide label');
  }

  if (!prefix) {
    throw Error('Please provide prefix');
  }
  return `${prefix}-${label.toLowerCase().replace(/\s/g, '_')}`;
};

interface ConnectSidebarTrackingProps {
  productArea: string;
  useLabelAsPageName?: boolean;
}

type SidebarTrackingParam = { label: string, onClick?: MouseEventHandler<any> | undefined; };

/**
 * HOC for injecting an onclick listener for tracking events on sidebar properties.
 * @param Component
 */
export const connectSidebarTracking = <P extends SidebarTrackingParam>(
  Component: ComponentType<P>,
): FC<P & ConnectSidebarTrackingProps> => (
    ({ label, useLabelAsPageName, productArea, onClick, ...other }) => (
      <Component
        {...other as unknown as P}
        data-qa={labelToQA({ label, prefix: 'sidebar-item' })}
        label={label}
        onClick={(event) => {
          onClick?.(event);
          recordTrack(NAVIGATION_EVENTS.OBJECT_CLICKED, {
            subject_name: useLabelAsPageName ?
              `Navigate To ${label}` :
              `Navigate To ${productArea} - ${label}`,
            product_area: productArea,
            product_sub_area: 'Sidebar Navigation',
            domain: 'app.saucelabs.com',
            page: 'Sidebar Navigation',
          });
        }}
      />
    )
  );

/**
 * Filters the DC list by available / enabled DCs for the user & current user's LD Flags.
 * @param dataCentersList
 */
export const useActiveDatacenterList = (dataCentersList: DataCenter[]) => {
  const { webUiUsEast4Dc } = useFlags();

  return !webUiUsEast4Dc ? (
    dataCentersList.filter((dc) => dc.value !== 'us-east-4')
  ) : dataCentersList;
};

// This list should be in sync with encore implementation:
// https://github.com/saucelabs/encore/blob/49f0e58a13f9d8dc0e11591ec99aa13c77d59cd2/encore/panel/views/app.py#L23-L37
export const ALLOWED_USER_TYPES = [
  userConstants.USER_TYPE_ADMIN,
  userConstants.USER_TYPE_FREE_TRIAL,
  userConstants.USER_TYPE_INVOICED,
  userConstants.USER_TYPE_POC,
];

/**
 * Returns `true` if Analytics menu item or component or any other visual element
 * should be visible.
 * @param {string} userType
 * @returns {boolean}
 */
export function isVisibleForUserType(userType: string) {
  return ALLOWED_USER_TYPES.includes(userType);
}

/**
 * Sets up an element / component to be a focus target for manual focus. Designed for skipping to /
 * from navigation elements.
 * @param id
 */
export const useFocusTarget = (id: SKIP_MENU_IDS) => {
  const { isFocusVisible } = useFocusVisible();

  const className = classnames('focus:outline-none', {
    'focus:outline-none focus:ring focus:ring-blue-100 ring-inset': isFocusVisible,
  });

  return {
    id,
    tabIndex: -1,
    className,
  };
};
